import {
    destroyStores,
    refreshBearcod,
    refreshStocks,
    useLegalEntities,
    useStores,
} from '@gozoki/api';
import { Button, IconButton, Menu, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { GridColDef, GridInitialState } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { ROUTE_BUILDER, STORES_ROUTES } from '../../utils/constants/routes.constants';

import { includesLowerCase } from '@gozoki/tools';
import { Refresh } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import StoreCSVExportButton from '../../components/buttons/StoreCSVExportButton';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import PlusIcon from '../../components/icons/PlusIcon';
import { useRights } from '../../utils/hooks';
import { fonts } from '../../utils/theme/fonts.theme';
import { getStoreTypeLabel } from '../../utils/types/toFrench';

const initialFilterState: GridInitialState = {
    filter: {
        filterModel: {
            items: [
                {
                    field: 'storeType',
                    operator: 'equals',
                    value: 'BOUTIQUE', // La valeur à filtrer
                },
            ],
        },
    },
};

const promoCampaignColumnDefs: GridColDef[] = [
    {
        field: 'label',
        headerName: 'Nom',
        flex: 1,
    },
    {
        field: 'address',
        headerName: 'Adresse',
        flex: 1,
        renderCell: (field) => field.value.address,
    },
    {
        field: 'checkpointStoreRef',
        headerName: 'Ref',
        flex: 1,
    },
    {
        field: 'storeType',
        headerName: 'Type de boutique',
        valueFormatter: (field) => getStoreTypeLabel(field.value),
        type: 'string',
        flex: 1,
        renderCell: (field) => getStoreTypeLabel(field.value),
    },
    {
        field: 'legalEntity',
        headerName: 'Entreprise légale',
        flex: 1,
    },
];

const StoresPage = () => {
    const isStockAdmin = useRights(['STOCK_ADMIN']);
    const [storeSearch, setStoreSearch] = useState('');
    const navigate = useNavigate();

    const [storesSelection, setStoresSelection] = useState<number[]>([]);
    const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<null | HTMLElement>(null);

    const stores = useStores();

    const activeStores = useMemo(() => {
        return (
            stores.data
                ?.filter((store) => store.isOnline && store.storeType === 'BOUTIQUE')
                .map((store) => store.id) ?? []
        );
    }, [stores.data]);

    const storesToExport = useMemo(() => {
        if (storesSelection.length > 0) {
            return storesSelection;
        }
        return activeStores;
    }, [storesSelection, activeStores]);

    const { data: legalEntities } = useLegalEntities();

    // ********** QUERY TO DATAGRID TRANSFORMATION *********** //

    const filteredStoreRows =
        stores.data?.filter((store) => includesLowerCase(store.label, storeSearch)) ?? [];

    const storeRows = filteredStoreRows.map((store) => ({
        ...store,
        legalEntity:
            legalEntities?.find((legalEntity) => legalEntity.id === store.legalEntityId)?.name ??
            'N/A',
    }));

    // *********************** HANDLERS  ********************* //

    const deleteStoresMut = useMutation({
        mutationFn: destroyStores,
    });

    const refreshStockMut = useMutation({
        mutationFn: refreshStocks,
    });
    const refreshBearcodMut = useMutation({
        mutationFn: refreshBearcod,
    });

    const navigateAndUpdateCampaign = async () => {
        navigate(ROUTE_BUILDER.updateStore(storesSelection[0]));
    };

    const navigateStoreDetails = (id: number) => navigate(ROUTE_BUILDER.storeDetails(id));

    const storesDisabled = storesSelection.length === 0 || deleteStoresMut.isPending;

    return (
        <Page sx={fonts.roboto}>
            <div style={{ display: 'flex' }}>
                <Typography style={fonts.pageTitle} flexGrow={1}>
                    Boutiques
                </Typography>
                <Button
                    variant="contained"
                    sx={{ marginRight: '16px' }}
                    onClick={() => navigate(STORES_ROUTES.ADD_STORE)}
                    disabled={!isStockAdmin}
                >
                    <PlusIcon />
                    Ajouter une boutique
                </Button>
                <IconButton
                    onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
                    disabled={!isStockAdmin}
                    sx={{
                        'backgroundColor': 'primary.main',
                        'color': 'white',
                        'width': 40,
                        'height': 40,
                        'borderRadius': '50%',
                        '&:hover': { backgroundColor: 'primary.dark' },
                        '&.Mui-disabled': { backgroundColor: 'action.disabledBackground' },
                    }}
                >
                    <Refresh />
                </IconButton>
                <Menu
                    anchorEl={refreshMenuAnchor}
                    open={Boolean(refreshMenuAnchor)}
                    onClose={() => setRefreshMenuAnchor(null)}
                >
                    <MenuItem
                        onClick={() => {
                            refreshStockMut.mutate();
                            setRefreshMenuAnchor(null);
                        }}
                        disabled={refreshStockMut.isPending}
                    >
                        Rafraîchir les stocks CheckPoint
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            refreshBearcodMut.mutate();
                            setRefreshMenuAnchor(null);
                        }}
                        disabled={refreshBearcodMut.isPending}
                    >
                        Rafraîchir les données Bearcod
                    </MenuItem>
                </Menu>
            </div>
            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                    <TextField
                        variant="outlined"
                        label="Recherche"
                        value={storeSearch}
                        onChange={(e) => setStoreSearch(e.target.value)}
                    />
                    <div style={{ flexGrow: 1 }} />

                    <Button
                        variant="contained"
                        sx={{ height: '36px', marginRight: '16px' }}
                        disabled={storesDisabled}
                        onClick={navigateAndUpdateCampaign}
                    >
                        Modifier
                    </Button>
                    <StoreCSVExportButton selectedStores={storesToExport} sx={{ height: '36px' }} />
                </div>
                <SimpleDataGrid
                    columns={promoCampaignColumnDefs}
                    rows={storeRows}
                    loading={stores.isLoading}
                    onRowSelectionModelChange={(newSelection) =>
                        setStoresSelection(newSelection as number[])
                    }
                    onRowClick={(row) => navigateStoreDetails(row.row.id)}
                    initialState={initialFilterState}
                />
            </Paper>
        </Page>
    );
};

export default StoresPage;
